import { useState, useEffect, useContext, useRef, useImperativeHandle } from "react";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import SoftBox from "components/SoftBox";
import SoftAvatar from "components/SoftAvatar";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftEditor from "components/SoftEditor";
import SoftButton from "components/SoftButton";
import { UserContext } from "context/AppProvider/UserContext";
import { fetchUserComments } from "redux/actions/flowDesigner/flowsActionCreator";
import { fetchUsers } from "redux/actions/flowDesigner/usersActionCreator";
import { insertComment, deleteComment, updateComment } from "api/userCommentApi";
import AlertDialog from "cxfcomponents/AlertDialog";
import typography from "assets/theme/base/typography";
import { TRIGGER_TYPES } from "constants/flowDesigner";

const ButtonsWrapper = styled(SoftBox)(() => ({
   flexShrink: 0,
   padding: "8px 0 8px 0",
   zIndex: 1000,
   display: "flex",
   gap: 15.5,
   "& button": {
      flex: 1,
      maxWidth: "100px"
   },
   justifyContent: "flex-end"
}));

const SOFT_EDITOR_MODULES = {
   toolbar: [[{ header: [1, 2, 3, 4, 5, 6, false] }], ["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }], ["link"], ["clean"]]
};
const Comments = ({ node_id, onDirtyChange, ref }) => {
   const [commentInputClicked, setCommentInputClicked] = useState(false);
   const [commentEditorValue, setCommentEditorValue] = useState("");
   const [openDeleteCommentDialog, setOpenDeleteCommentDialog] = useState(false);
   const [deleteCommentId, setDeleteCommentId] = useState(null);
   const [editCommentId, setEditCommentId] = useState(null);
   const [editCommentValue, setEditCommentValue] = useState(null);
   const { user_comments, isLoadingUserComments, currentSavedFlow } = useSelector((state) => state?.designer?.flows);
   const { users } = useSelector((state) => state?.designer?.users);
   const { userData } = useContext(UserContext);
   const { size } = typography;
   const dispatch = useDispatch();
   const commentContainerRef = useRef(null);
   const isNodeComment = !!node_id;
   const triggerTypes = [TRIGGER_TYPES.REST_ENDPOINT, TRIGGER_TYPES.WEBHOOK, TRIGGER_TYPES.SCHEDULE, TRIGGER_TYPES.BLUE_PRISM_DELTA];
   const isTriggerComment = triggerTypes.includes(node_id);
   const flow_id = currentSavedFlow?.id;
   const { selectedSchedule } = useSelector((store) => store?.designer?.triggers);
   const isDirty = commentInputClicked && commentEditorValue.trim() !== "";

   const handleCommentInputClick = () => {
      setCommentInputClicked(true);
   };

   const isQuillEmpty = (value) => {
      if (value.replace(/<(.|\n)*?>/g, "").trim().length === 0 && !value.includes("<img")) {
         return true;
      }
      return false;
   };

   const handleCommentEditorChange = (value) => {
      if (isQuillEmpty(value)) {
         setCommentEditorValue("");
      } else {
         setCommentEditorValue(value);
      }
   };

   const handleAddComment = async () => {
      const input = {
         comment: commentEditorValue,
         comment_type: isTriggerComment ? "trigger" : selectedSchedule ? "schedule" : isNodeComment ? "node" : "flow",
         flow_id: flow_id,
         node_id: isNodeComment ? node_id : null,
         user_id: userData?.auth0_id,
         tenant_id: userData?.tenant_id
      };

      const result = await insertComment(input);

      if (result.isSuccessful) {
         dispatch(fetchUserComments(userData?.tenant_id, flow_id, node_id));
      }

      setCommentInputClicked(false);
      setCommentEditorValue("");
   };

   const handleCancelComment = () => {
      onDirtyChange?.("cancel");
   };

   const handleDeleteComment = async (id) => {
      setDeleteCommentId(id);
      setOpenDeleteCommentDialog(true);
   };

   const handleEditComment = (id, comment) => {
      setEditCommentId(id);
      setEditCommentValue(comment);
   };

   const handleEditCommentEditorChange = (value) => {
      if (isQuillEmpty(value)) {
         setEditCommentValue("");
      } else {
         setEditCommentValue(value);
      }
   };

   const handleCancelEditComment = () => {
      setEditCommentId(null);
      setEditCommentValue(null);
   };

   const handleDeleteCommentAccept = async () => {
      const deleteCommentResponse = await deleteComment(userData?.tenant_id, deleteCommentId);

      if (deleteCommentResponse.isSuccessful) {
         dispatch(fetchUserComments(userData?.tenant_id, flow_id, node_id));
      }
      setOpenDeleteCommentDialog(false);
   };

   const handleCloseDeleteCommentDialog = () => {
      setDeleteCommentId(null);
      setOpenDeleteCommentDialog(false);
   };

   const handleSaveEditComment = async () => {
      if (editCommentId && editCommentValue) {
         const updateCommentResponse = await updateComment({
            comment: editCommentValue,
            id: editCommentId,
            tenant_id: userData?.tenant_id
         });

         if (updateCommentResponse.isSuccessful) {
            dispatch(fetchUserComments(userData?.tenant_id, flow_id, node_id));
            setEditCommentId(null);
            setEditCommentValue(null);
         }
      }
   };

   const renderUserComments = () => {
      if (isLoadingUserComments) return;

      return user_comments
         .sort((a, b) => new Date(b.created_date_time) - new Date(a.created_date_time))
         .map(({ id, comment, user_id, created_date_time }, key) => {
            const user = users?.find((user) => user.auth0_id === user_id);

            return (
               <SoftBox key={id} display="flex" mt={key === 0 ? 0 : 3}>
                  <SoftBox flexShrink={0}>
                     <SoftAvatar src={user?.image} alt={`${user?.first_name} ${user?.last_name}`} size="sm" />
                  </SoftBox>
                  <SoftBox display="flex" flexDirection="column" flexGrow={1} ml={2}>
                     <SoftBox display="flex" gap={2} alignItems="end">
                        <SoftTypography
                           variant="h6"
                           fontWeight="medium"
                           textTransform="capitalize"
                           sx={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              maxWidth: "200px" // Adjust the maxWidth as needed
                           }}
                        >
                           {user?.first_name}&nbsp;{user?.last_name}
                        </SoftTypography>
                        <SoftTypography variant="h6" fontWeight="regular" textTransform="capitalize" sx={{ fontSize: size.sm }}>
                           {created_date_time && moment(created_date_time).format("MMMM D, YYYY [at] h:mm A")}
                        </SoftTypography>
                     </SoftBox>
                     {editCommentId === id ? (
                        <SoftBox mt={1.5}>
                           <SoftEditor className="comment-editor" value={editCommentValue} onChange={handleEditCommentEditorChange} placeholder="Write your comment" modules={SOFT_EDITOR_MODULES} />
                           <SoftBox display="flex" gap={2} mt={1}>
                              <SoftButton variant="text" color="info" sx={{ padding: 0, minWidth: 0, width: "max-content" }} onClick={handleSaveEditComment} disabled={!editCommentValue.trim()}>
                                 Save
                              </SoftButton>
                              <SoftButton className="btn-cancel-edit-comment" variant="text" color="secondary" sx={{ padding: 0, minWidth: 0, width: "max-content" }} onClick={handleCancelEditComment}>
                                 Cancel
                              </SoftButton>
                           </SoftBox>
                        </SoftBox>
                     ) : (
                        <>
                           <SoftEditor className="documentation preview" theme="bubble" value={comment} readOnly />
                           <SoftBox display="flex" gap={2} mt={1.5}>
                              <SoftButton
                                 className="btn-edit-comment"
                                 variant="text"
                                 color="info"
                                 sx={{
                                    padding: 0,
                                    minWidth: 0,
                                    width: "max-content",
                                    fontSize: "12px !important"
                                 }}
                                 onClick={() => handleEditComment(id, comment)}
                              >
                                 Edit
                              </SoftButton>
                              <SoftButton
                                 className="btn-delete-comment"
                                 variant="text"
                                 color="secondary"
                                 sx={{
                                    padding: 0,
                                    minWidth: 0,
                                    width: "max-content",
                                    fontSize: "12px !important"
                                 }}
                                 onClick={() => handleDeleteComment(id)}
                              >
                                 Delete
                              </SoftButton>
                           </SoftBox>
                        </>
                     )}
                  </SoftBox>
               </SoftBox>
            );
         });
   };

   useEffect(() => {
      dispatch(fetchUserComments(userData?.tenant_id, flow_id, node_id));
   }, [flow_id, node_id]);

   useEffect(() => {
      if (commentInputClicked && commentContainerRef?.current) {
         commentContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
   }, [commentInputClicked]);

   useEffect(() => {}, [isLoadingUserComments, user_comments]);

   useEffect(() => {
      onDirtyChange?.(isDirty);
   }, [isDirty]);

   useEffect(() => {
      if (!users.length) {
         dispatch(fetchUsers(userData?.tenant_id));
      }
   }, []);

   useImperativeHandle(
      ref,
      () => ({
         reset: () => {
            setCommentInputClicked(false);
            setCommentEditorValue("");
         }
      }),
      []
   );

   return (
      <>
         <Box className="user-comments" sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
            <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }} ref={commentContainerRef}>
               <SoftBox display="flex" alignItems="center" mt={3} mb={3} sx={{ position: commentInputClicked ? "relative" : "sticky", top: 0, zIndex: 1 }}>
                  <SoftBox flexShrink={0} mr={2} alignSelf="start">
                     <SoftAvatar src={userData?.image} alt="profile picture" size="sm" />
                  </SoftBox>
                  <SoftBox flexGrow={1}>
                     {commentInputClicked ? (
                        <SoftEditor className="comment-editor" value={commentEditorValue} onChange={handleCommentEditorChange} placeholder="Write your comment" modules={SOFT_EDITOR_MODULES} />
                     ) : (
                        <SoftBox>
                           <SoftInput className="comments-input" placeholder="Write your comment" onClick={handleCommentInputClick} />
                        </SoftBox>
                     )}
                  </SoftBox>
               </SoftBox>
               {renderUserComments()}
            </Box>
            <ButtonsWrapper>
               <SoftButton variant="outlined" color="info" onClick={handleCancelComment}>
                  Cancel
               </SoftButton>
               <SoftButton color="primary" disabled={!commentEditorValue.trim()} onClick={handleAddComment}>
                  Save
               </SoftButton>
            </ButtonsWrapper>
         </Box>
         <AlertDialog
            open={openDeleteCommentDialog}
            onClose={handleCloseDeleteCommentDialog}
            onAccept={handleDeleteCommentAccept}
            acceptButtonText="Delete"
            dialogTitle="Delete Comment"
            dialogContent="Are you sure you want to delete this comment?"
         />
      </>
   );
};

export default Comments;
